import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { TableColumn } from '@swimlane/ngx-datatable';
import { ButtonStyle } from 'src/app/components/system/modal-confirm/modal-confirm.component';
import { Filter, FilterBuilderOutput, FilterBuilderParam } from 'src/app/models/filter-builder';
import { ColumnOption, GridSettings } from 'src/app/models/grid-settings';
import { ApplicationLogEmailListItem, GetLogEmailsRequest, LogEmailVM } from 'src/app/models/log-email';
import { SystemService } from 'src/app/services';

@Component({
    selector: 'wm-application-log-emails-list',
    templateUrl: './application-log-emails-list.component.html',
    styleUrl: './application-log-emails-list.component.css',
    standalone: false
})
export class ApplicationLogEmailsListComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('applicationActivityLinkTmpl', { static: true }) applicationActivityLinkTmpl: TemplateRef<any>;
  @ViewChild('logEmailTmpl', { static: true }) logEmailTmpl: TemplateRef<any>;
  @ViewChild('erroredTmpl', { static: true }) erroredTmpl: TemplateRef<any>;
  
  @Input() clientId: string = null;
  
  componentId: string;
  logEmails: ApplicationLogEmailListItem[];
  logEmail: LogEmailVM;
  logEmailLoading = true;
  ButtonStyle = ButtonStyle;
  searchLoading = true;
  columns: TableColumn[];
  availableColumns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  respondToClientIdChanges = false;

  page = {
    // The number of elements in the page
    size: 10,
    // The total number of elements
    totalElements: 0,
    // The total number of pages
    totalPages: 0,
    // The current page number
    pageNumber: 0
  };

  sortField: string;
  sortDescending: boolean;
  params: FilterBuilderParam[] = [
    {
      id: 'applicationNumber',
      name: 'Application Number'
    },
    {
      id: 'workflowName',
      name: 'Workflow'
    },
    {
      id: 'activityName',
      name: 'Activity'
    },
    {
      id: 'subject',
      name: 'Subject'
    },
    {
      id: 'body',
      name: 'Body'
    },
    {
      id: 'recipients',
      name: 'Recipient(s)'
    },
    {
      id: 'errorStatus',
      name: 'Error Status'
    }
  ];
  filters: FilterBuilderOutput = {};
  // column prop strings to be hidden on the grid by default
  defaultHiddenColumns: string[] = [];
  // column prop strings that should have no option to hide in the grid, and are not exportable (such as button columns like Refund or Actions)
  actionColumns: string[] = [
    'view'
  ];
  // column prop strings to be excluded from exports by default
  defaultExportExludedColumns: string[] = [];
  //showDateErrorMessage = false;

  constructor(
    private _systemSvc: SystemService
  ) {}

  ngOnInit() {
    this.columns = [
      {
        prop: 'view',
        name: '',
        cellTemplate: this.logEmailTmpl
      },
      {
        prop: 'applicationNumber',
        name: 'Application Number',
        cellTemplate: this.applicationActivityLinkTmpl
      },
      {
        prop: 'clientName',
        name: 'Jurisdiction' 
      },
      {
        prop: 'workflowName',
        name: 'Workflow',
      },
      {
        prop: 'activityName',
        name: 'Activity'
      },
      {
        prop: 'subject',
        name: 'Subject'
      },
      {
        prop: 'scheduledForString',
        name: 'Scheduled For'
      },
      {
        prop: 'canceledOnString',
        name: 'Canceled On'
      },
      {
        prop: 'sentOnString',
        name: 'Sent On'
      },
      {
        prop: 'delivered',
        name: 'Delivered'
      },
      {
        prop: 'errored',
        name: 'Errored',
        cellTemplate: this.erroredTmpl
      }
    ];

    this.setAvailableColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.respondToClientIdChanges) {
      this.getLogEmails();
    }
  }
  
  ngAfterViewInit() {
    this.respondToClientIdChanges = true;
  }

  getLogEmails() {
    this.searchLoading = true;

    let allFilters: Filter[] = [];

    for (const id in this.filters) {
      if (this.filters[id]) {
        allFilters.push(this.filters[id]);
      }
    }

    const getLogEmailsRequest = new GetLogEmailsRequest({
      pageSize: this.page.size,
      pageNumber: this.page.pageNumber,
      filters: allFilters,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    });

    this._systemSvc.getApplicationLogEmails(getLogEmailsRequest, this.clientId).subscribe(response => {
      if (response) {
        this.logEmails = response.logEmails;
        this.page.totalElements = response.recordCount;
        this.page.totalPages = response.pageCount;
      } 

      this.searchLoading = false;
    });
  }
    
  getLogEmail(logEmailId: number) {
    this.logEmailLoading = true;
    this.logEmail = null;
    
    this._systemSvc.getLogEmail(logEmailId).subscribe(logEmail => {
      if (logEmail) {
        this.logEmail = logEmail;
      }
      
      this.logEmailLoading = false;
    });
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  handleSettingsChanged(event: GridSettings) {
    this.filters = event.filters;
    this.page.size = event.pageSize;
    this.page.pageNumber = event.pageNumber;
    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending;
    this.columnOptions = event.columnOptions;

    const visibleColumnProps = this.columnOptions
      .filter(co => co.checked)
      .map(co => co.name);
    this.columns = this.availableColumns.filter(
      c =>
        visibleColumnProps.includes(c.prop as string) ||
        this.actionColumns.includes(c.prop as string)
    );

    this.getLogEmails();
  }

  handleColumnOptionsChanged(event: ColumnOption[]) {
    this.columnOptions = event;
  }
}
